import type {
  FetchBaseQueryError,
  QueryReturnValue,
} from '@reduxjs/toolkit/query'
import type {
  AuthErrorResponse,
  LoginRequest,
  LogoutResponse,
  PlatformMeResponse,
  TokenResponse,
} from '@/types/api/auth'
import type { SuperadminSession } from '@/types/domain'
import { mapPlatformMeToSession } from '@/utils/authMappers'
import {
  clearAccessToken,
  hasAccessToken,
  writeAccessToken,
} from '@/utils/tokenStorage'
import { AUTH_PATHS } from './authPaths'
import { baseApi } from './baseApi'

function toAuthError(message: string): { error: FetchBaseQueryError } {
  return {
    error: {
      status: 401,
      data: { message } satisfies AuthErrorResponse,
    },
  }
}

function asSessionResult(
  result: QueryReturnValue<unknown, FetchBaseQueryError, object>,
): QueryReturnValue<SuperadminSession, FetchBaseQueryError, object> {
  if (result.error) {
    return { error: result.error }
  }
  return {
    data: mapPlatformMeToSession(result.data as PlatformMeResponse),
  }
}

function asLogoutResult(
  result: QueryReturnValue<unknown, FetchBaseQueryError, object>,
): QueryReturnValue<LogoutResponse, FetchBaseQueryError, object> {
  if (result.error && result.error.status !== 204) {
    return { error: result.error }
  }
  return { data: { ok: true } }
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSession: builder.query<SuperadminSession, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        if (!hasAccessToken()) {
          const refreshResult = await baseQuery({
            url: AUTH_PATHS.refresh,
            method: 'POST',
          })
          if (refreshResult.error || !refreshResult.data) {
            return toAuthError('Session not found.')
          }
          writeAccessToken((refreshResult.data as TokenResponse).accessToken)
        }

        const result = await baseQuery({ url: AUTH_PATHS.session, method: 'GET' })
        return asSessionResult(result)
      },
      providesTags: [{ type: 'Session', id: 'CURRENT' }],
    }),
    login: builder.mutation<SuperadminSession, LoginRequest>({
      queryFn: async (credentials, _api, _extraOptions, baseQuery) => {
        const loginResult = await baseQuery({
          url: AUTH_PATHS.login,
          method: 'POST',
          body: credentials,
        })

        if (loginResult.error) {
          return { error: loginResult.error }
        }

        writeAccessToken((loginResult.data as TokenResponse).accessToken)

        const meResult = await baseQuery({ url: AUTH_PATHS.session, method: 'GET' })
        if (meResult.error) {
          clearAccessToken()
          return { error: meResult.error }
        }

        return asSessionResult(meResult)
      },
      invalidatesTags: [{ type: 'Session', id: 'CURRENT' }],
    }),
    logout: builder.mutation<LogoutResponse, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: AUTH_PATHS.logout,
          method: 'POST',
        })

        clearAccessToken()

        if (result.error && result.error.status !== 204) {
          return asLogoutResult(result)
        }

        return { data: { ok: true } }
      },
      invalidatesTags: [{ type: 'Session', id: 'CURRENT' }],
    }),
  }),
})

export const {
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useLoginMutation,
  useLogoutMutation,
} = authApi
